import { belowLargeTablet, Button, IconButton, Tooltip } from "@ameelio/ui";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ClosedCaptionDisabledOutlined from "@mui/icons-material/ClosedCaptionDisabledOutlined";
import ClosedCaptionOutlined from "@mui/icons-material/ClosedCaptionOutlined";
import Dialpad from "@mui/icons-material/Dialpad";
import ExitToAppOutlined from "@mui/icons-material/ExitToAppOutlined";
import GridViewOutlined from "@mui/icons-material/GridViewOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import ScreenShareOutlined from "@mui/icons-material/ScreenShareOutlined";
import ViewAgendaOutlined from "@mui/icons-material/ViewAgendaOutlined";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import {
  alpha,
  Badge,
  Box,
  useMediaQuery as measureScreenWidth,
} from "@mui/material";
import { common, grey, red } from "@mui/material/colors";
import { SystemStyleObject, Theme } from "@mui/system";
import { useTranslation } from "react-i18next";
import IconWithSlash from "../../lib/IconWithSlash.tsx";
import { lightPalette, ltrTheme } from "../../theme";
import MediaControls, { Props as MediaControlProps } from "../MediaControls";
import { CallLayoutMode } from "./CallLayoutMode";

const offStyle: SystemStyleObject<Theme> = {
  "&:hover": {
    backgroundColor: lightPalette.primary.dark,
    color: common.white,
  },
};

type Props = MediaControlProps & {
  onLeave: () => void;
  onToggleChat?: () => void;
  onToggleRaiseHand?: () => void;
  backgroundSelectionDisabled?: boolean;
  onToggleBackgroundSelection?: () => void;
  leaveCallDisabled?: boolean;
  disabledReason?: string;
  alerts: boolean;
  isScreensharing?: boolean;
  onToggleAlerts?: () => void;
  screenshareDisabled?: boolean;
  onToggleScreenshare?: () => void;
  layoutMode?: CallLayoutMode;
  layoutToggleDisabled?: boolean;
  onToggleLayout?: () => void;
  messageCount?: number;
  leaveCallLabel: string;
  styleOverrides?: SystemStyleObject<Theme>;
  onToggleCaptions?: () => void;
  keyboardOpened?: boolean;
  onToggleKeyboard?: () => void;
  captions?: boolean;
  captionsAvailable?: boolean;
  captionsDisabled?: boolean;
};

export default function CallControls({
  onToggleChat,
  onToggleAlerts,
  leaveCallDisabled,
  disabledReason,
  backgroundSelectionDisabled,
  onToggleBackgroundSelection,
  screenshareDisabled,
  onToggleScreenshare,
  layoutMode,
  layoutToggleDisabled,
  onToggleLayout,
  alerts,
  isScreensharing,
  messageCount,
  onLeave,
  leaveCallLabel,
  styleOverrides,
  captions,
  onToggleCaptions,
  captionsAvailable,
  captionsDisabled,
  keyboardOpened,
  onToggleKeyboard,
  ...mediaControlProps
}: Props) {
  const { t } = useTranslation();
  const isMobileOrSmallTablet = belowLargeTablet(measureScreenWidth);
  return (
    <Box
      sx={{
        background: (th) => alpha(th.palette.background.default, 0.8),
        borderRadius: "10000px",
        px: 2,
        py: 1.5,
        backdropFilter: "blur(5px)",
        height: ltrTheme.spacing(8),
        "> span": {
          my: 0,
          mx: { xs: 0.5, sm: 2 },
        },
        "> span:last-child": {
          mt: 0,
          mr: 0,
          mb: 0,
          ml: 1,
        },
        whiteSpace: "nowrap",
        ...(styleOverrides || {}),
      }}
    >
      <MediaControls {...mediaControlProps} />
      {onToggleCaptions && captionsAvailable && (
        <IconButton
          tooltip
          sx={{
            backgroundColor: () =>
              !captions ? lightPalette.primary.main : "auto",
            color: !captions ? common.white : "auto",
            ...(!captions ? offStyle : {}),
          }}
          ariaLabel={
            captionsDisabled
              ? t("Captions are disabled")
              : captions
                ? t("Turn off captions")
                : t("Turn on captions")
          }
          disabled={captionsDisabled}
          onClick={onToggleCaptions}
        >
          {captions ? (
            <ClosedCaptionOutlined />
          ) : (
            <ClosedCaptionDisabledOutlined />
          )}
        </IconButton>
      )}
      {onToggleScreenshare && (
        <IconButton
          tooltip
          disabled={screenshareDisabled}
          onClick={onToggleScreenshare}
          ariaLabel={
            screenshareDisabled
              ? t("Screensharing is disabled")
              : isScreensharing
                ? t("Stop sharing")
                : t("Share screen")
          }
          sx={{
            backgroundColor: () => (isScreensharing ? grey[700] : "auto"),
            color: isScreensharing ? common.white : "auto",
            ...(isScreensharing
              ? {
                  "&:hover": {
                    backgroundColor: grey[700],
                  },
                }
              : {}),
          }}
        >
          {isScreensharing ? <ScreenShareOutlined /> : <ScreenShareOutlined />}
        </IconButton>
      )}
      {onToggleLayout && (
        <IconButton
          tooltip
          disabled={layoutToggleDisabled}
          onClick={onToggleLayout}
          ariaLabel={
            layoutToggleDisabled
              ? t("Layout switching is disabled")
              : layoutMode === CallLayoutMode.Grid
                ? t("Switch to spotlight view")
                : t("Switch to grid view")
          }
        >
          {layoutMode === CallLayoutMode.Grid ? (
            <GridViewOutlined />
          ) : (
            <ViewAgendaOutlined />
          )}
        </IconButton>
      )}
      {onToggleChat && (
        <IconButton tooltip ariaLabel={t("Open chat")} onClick={onToggleChat}>
          <Badge
            variant={messageCount ? "standard" : "dot"}
            invisible={!messageCount}
            color="primary"
            badgeContent={messageCount}
          >
            <ChatOutlinedIcon />
          </Badge>
        </IconButton>
      )}
      {onToggleAlerts && (
        <IconButton
          tooltip
          ariaLabel={alerts ? t("Turn off alerts") : t("Turn on alerts")}
          onClick={() => {
            onToggleAlerts();
          }}
          sx={{
            backgroundColor: () =>
              !alerts ? lightPalette.primary.main : "auto",
            color: !alerts ? common.white : "auto",
            ...(!alerts ? offStyle : {}),
          }}
        >
          {alerts ? <NotificationsNoneIcon /> : <NotificationsOffIcon />}
        </IconButton>
      )}
      {onToggleKeyboard && (
        <IconButton
          tooltip
          ariaLabel={keyboardOpened ? t("Close dialpad") : t("Open dialpad")}
          onClick={() => {
            onToggleKeyboard();
          }}
          sx={{
            backgroundColor: () =>
              !keyboardOpened ? lightPalette.primary.main : "auto",
            color: !keyboardOpened ? common.white : "auto",
            ...(!keyboardOpened ? offStyle : {}),
          }}
        >
          <IconWithSlash slashed={!keyboardOpened}>
            <Dialpad />
          </IconWithSlash>
        </IconButton>
      )}
      {onToggleBackgroundSelection && (
        <IconButton
          tooltip
          disabled={backgroundSelectionDisabled}
          ariaLabel={
            backgroundSelectionDisabled
              ? t("Custom backgrounds are disabled")
              : t("Set custom background")
          }
          onClick={() => onToggleBackgroundSelection()}
        >
          <WallpaperIcon />
        </IconButton>
      )}
      <Tooltip
        title={disabledReason || t("Call cannot be left yet")}
        disabled={!leaveCallDisabled}
      >
        {/* Disabled buttons don't fire events, wrapping in span below for the tooltip display */}
        <span>
          {isMobileOrSmallTablet ? (
            <IconButton
              tooltip
              onClick={onLeave}
              ariaLabel={leaveCallLabel}
              disabled={leaveCallDisabled}
              sx={{
                backgroundColor: red[700],
                color: common.white,
                "&:hover": {
                  backgroundColor: lightPalette.error.main,
                  color: common.white,
                },
              }}
            >
              <ExitToAppOutlined />
            </IconButton>
          ) : (
            <Button
              onClick={onLeave}
              disabled={leaveCallDisabled}
              sx={{
                whiteSpace: "nowrap",
                backgroundColor: red[700],
                borderRadius: "10000px",
                color: common.white,
                "&:hover": {
                  backgroundColor: lightPalette.error.main,
                },
              }}
            >
              {leaveCallLabel}
            </Button>
          )}
        </span>
      </Tooltip>
    </Box>
  );
}
